import { useState } from 'react';
import { Bot, Mail, MapPin, MessageSquare, Phone, Send, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useBubbly } from '@/components/bubbly/BubblyContext';
import { cn } from '@/lib/utils';
import {
  SUPPORT_ADDRESS_LINE1,
  SUPPORT_ADDRESS_LINE2,
  SUPPORT_EMAIL,
  SUPPORT_PHONE,
  SUPPORT_PHONE_TOLL_FREE,
  SUPPORT_TEL_HREF,
  SUPPORT_TOLL_FREE_HREF,
} from '@/lib/constants';

type Channel = 'email' | 'call' | 'chat';
type Variant = 'header' | 'sidebar' | 'inline';

interface ContactUsMenuProps {
  variant?: Variant;
  /** Trigger label override (used by the 'inline' variant on /invoices). */
  triggerLabel?: string;
  /** Channel to open with. Defaults to 'email'. */
  initialChannel?: Channel;
}

/**
 * "Contact us" popover with three channels (email form, call rows, chat
 * placeholder). Three visual variants:
 *   - 'header'  — navy-on-grey chip, popover anchors top-right
 *   - 'sidebar' — sidebar nav-item, popover opens to the right of the sidebar
 *   - 'inline'  — primary-styled "Chat with us" button used in page footers,
 *                 popover opens centered below the trigger
 */
export function ContactUsMenu({
  variant = 'sidebar',
  triggerLabel,
  initialChannel = 'email',
}: ContactUsMenuProps = {}) {
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState<Channel>(initialChannel);
  const { openBubbly } = useBubbly();

  const headerVariant = variant === 'header';
  const inlineVariant = variant === 'inline';
  const sidebarVariant = variant === 'sidebar';

  return (
    <>
      <div className={cn('relative', sidebarVariant && 'px-3', inlineVariant && 'inline-block')}>
        {headerVariant && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-haspopup="dialog"
            className="flex cursor-pointer items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.06] px-3 py-2 text-[13px] text-white/80 backdrop-blur-md transition-colors duration-200 hover:border-white/15 hover:bg-white/[0.12] hover:text-white"
          >
            <Mail size={14} /> {triggerLabel ?? 'Contact us'}
          </button>
        )}
        {sidebarVariant && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-haspopup="dialog"
            className={cn(
              'group relative flex w-full cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm transition-all duration-200',
              open
                ? 'bg-brix-primary-tint font-semibold text-brix-primary'
                : 'hover:bg-brix-primary-tint/60 font-medium text-brix-ink hover:translate-x-0.5',
            )}
          >
            <Mail size={18} strokeWidth={open ? 2 : 1.75} />
            {triggerLabel ?? 'Contact us'}
          </button>
        )}
        {inlineVariant && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-haspopup="dialog"
            className="inline-flex cursor-pointer items-center gap-2 rounded-full border border-brix-border bg-brix-surface px-4 py-2 text-sm font-semibold text-brix-primary shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card-hover"
          >
            <MessageSquare size={15} /> {triggerLabel ?? 'Chat with us'}
          </button>
        )}

        {open && (
          <>
            <div className="fixed inset-0 z-40" aria-hidden onClick={() => setOpen(false)} />
            <div
              role="dialog"
              aria-label="Contact us"
              className={cn(
                'absolute z-50 w-[22rem] overflow-hidden rounded-xl border border-brix-border bg-brix-surface shadow-card-hover',
                headerVariant && 'right-0 top-full mt-2',
                sidebarVariant && 'left-full top-0 ml-2',
                inlineVariant && 'left-1/2 top-full mt-2 -translate-x-1/2',
              )}
            >
              <div className="bg-brix-surface-alt/40 flex items-center justify-between border-b border-brix-border px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-brix-ink">Contact us</p>
                  <p className="mt-0.5 text-[11px] text-brix-muted">
                    Hey — pick the channel that works best for you.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="hover:bg-brix-primary-tint/60 flex h-7 w-7 cursor-pointer items-center justify-center rounded-md text-brix-muted transition-colors hover:text-brix-ink"
                >
                  <X size={14} />
                </button>
              </div>

              <div className="flex gap-1 px-3 pt-3">
                <ChannelTab
                  id="email"
                  active={channel}
                  onClick={setChannel}
                  icon={Mail}
                  label="Email"
                />
                <ChannelTab
                  id="call"
                  active={channel}
                  onClick={setChannel}
                  icon={Phone}
                  label="Call"
                />
                <ChannelTab
                  id="chat"
                  active={channel}
                  onClick={setChannel}
                  icon={MessageSquare}
                  label="Chat"
                />
              </div>

              <div className="px-4 py-4">
                {channel === 'email' && <EmailForm onSent={() => setOpen(false)} />}
                {channel === 'call' && <CallPanel />}
                {channel === 'chat' && (
                  <ChatPanel
                    onOpenChat={() => {
                      setOpen(false);
                      openBubbly();
                    }}
                  />
                )}
              </div>

              <div className="bg-brix-surface-alt/40 border-t border-brix-border px-4 py-3">
                <p className="flex items-start gap-1.5 text-[11px] text-brix-muted">
                  <MapPin size={11} className="mt-0.5 shrink-0 text-brix-primary" />
                  <span>
                    {SUPPORT_ADDRESS_LINE1}
                    <br />
                    {SUPPORT_ADDRESS_LINE2}
                  </span>
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

interface ChannelTabProps {
  id: Channel;
  active: Channel;
  onClick: (id: Channel) => void;
  icon: typeof Mail;
  label: string;
  badge?: string;
}

function ChannelTab({ id, active, onClick, icon: Icon, label, badge }: ChannelTabProps) {
  const isActive = active === id;
  return (
    <button
      type="button"
      onClick={() => onClick(id)}
      className={cn(
        'flex flex-1 cursor-pointer items-center justify-center gap-1.5 rounded-lg px-2 py-1.5 text-xs transition-colors',
        isActive
          ? 'bg-brix-primary-tint font-semibold text-brix-primary'
          : 'hover:bg-brix-primary-tint/40 text-brix-muted hover:text-brix-ink',
      )}
    >
      <Icon size={13} />
      {label}
      {badge && (
        <span className="ml-0.5 text-[9px] font-semibold tracking-tight text-brix-muted">
          {badge}
        </span>
      )}
    </button>
  );
}

function EmailForm({ onSent }: { onSent: () => void }) {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const params = new URLSearchParams();
    if (subject.trim()) params.set('subject', subject.trim());
    if (message.trim()) params.set('body', message.trim());
    const qs = params.toString();
    const href = `mailto:${SUPPORT_EMAIL}${qs ? `?${qs}` : ''}`;
    window.location.href = href;
    onSent();
  }

  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <p className="text-xs leading-relaxed text-brix-muted">
        Sends to{' '}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="font-medium text-brix-primary hover:underline"
        >
          {SUPPORT_EMAIL}
        </a>{' '}
        via your email app.
      </p>

      <div className="space-y-1.5">
        <Label htmlFor="cu-subject">Subject</Label>
        <Input
          id="cu-subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Order question, delivery issue, etc."
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="cu-msg">Message</Label>
        <textarea
          id="cu-msg"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="What can we help with?"
          rows={4}
          className={cn(
            'w-full rounded-lg border border-brix-border bg-brix-surface px-3 py-2 text-sm',
            'focus-visible:ring-brix-primary/30 focus-visible:border-brix-primary focus-visible:outline-none focus-visible:ring-2',
            'resize-none',
          )}
        />
      </div>

      <Button type="submit" size="md" className="w-full">
        <Send size={13} /> Open email
      </Button>
    </form>
  );
}

function CallPanel() {
  return (
    <div className="space-y-3">
      <p className="text-xs leading-relaxed text-brix-muted">
        Hey — you can call us locally or toll-free during business hours.
      </p>

      <a
        href={SUPPORT_TEL_HREF}
        className="hover:border-brix-primary/40 hover:bg-brix-primary-tint/30 block cursor-pointer rounded-lg border border-brix-border bg-brix-surface px-3 py-2.5 transition-colors"
      >
        <div className="text-[10px] font-semibold tracking-tight text-brix-muted">Local</div>
        <div className="mt-0.5 flex items-center gap-1.5 font-mono text-sm font-semibold text-brix-ink">
          <Phone size={12} className="text-brix-primary" /> {SUPPORT_PHONE}
        </div>
      </a>

      <a
        href={SUPPORT_TOLL_FREE_HREF}
        className="hover:border-brix-primary/40 hover:bg-brix-primary-tint/30 block cursor-pointer rounded-lg border border-brix-border bg-brix-surface px-3 py-2.5 transition-colors"
      >
        <div className="text-[10px] font-semibold tracking-tight text-brix-muted">Toll-free</div>
        <div className="mt-0.5 flex items-center gap-1.5 font-mono text-sm font-semibold text-brix-ink">
          <Phone size={12} className="text-brix-primary" /> {SUPPORT_PHONE_TOLL_FREE}
        </div>
      </a>
    </div>
  );
}

function ChatPanel({ onOpenChat }: { onOpenChat: () => void }) {
  return (
    <div className="space-y-3">
      <div className="bg-brix-surface-alt/40 space-y-2 rounded-lg border border-brix-border px-4 py-4 text-center">
        <Bot size={20} className="mx-auto text-brix-primary" />
        <p className="text-sm font-semibold text-brix-ink">Meet Bubbly</p>
        <p className="text-xs leading-relaxed text-brix-muted">
          Bubbly is our built-in assistant — instant answers about gas cylinder rentals, soda-machine
          troubleshooting, ordering &amp; delivery, and billing, drawn from the Brix help library.
        </p>
      </div>
      <Button type="button" size="md" className="w-full" onClick={onOpenChat}>
        <MessageSquare size={14} /> Ask Bubbly
      </Button>
      <p className="text-center text-[11px] leading-relaxed text-brix-muted">
        For anything Bubbly can't answer, it'll point you to email or a service request.
      </p>
    </div>
  );
}
